import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useAppSelector } from 'src/hooks/useStore';
import {
  getOrganizationMemberLabel,
  getOrganizationMembers,
  type OrganizationMember,
} from 'src/services/organizations.service';

interface AssignModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (user: OrganizationMember) => void;
  onRemove?: (user: OrganizationMember) => void;
  _session: any;
}

const AssignModal = ({
  open,
  onClose,
  onAssign,
  onRemove,
  _session,
}: AssignModalProps) => {
  const [users, setUsers] = useState<OrganizationMember[]>([]);
  const [selectedUser, setSelectedUser] = useState<OrganizationMember | null>(
    null,
  );
  const [_loading, setLoading] = useState(false);
  const { getIdTokenClaims } = useAuth0();
  const selectedOrganization = useAppSelector(
    state => state.organization.selectedOrganization,
  );
  const liveData = useAppSelector(state => state.sliceTransaction.liveData);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedOrganization?.id) {
        setUsers([]);
        setSelectedUser(null);
        return;
      }
      setLoading(true);
      try {
        const { __raw } = (await getIdTokenClaims()) as any;
        const members = await getOrganizationMembers(
          selectedOrganization.id,
          __raw,
          { liveData },
        );
        setUsers(members);

        if (_session?.assignedUser?.user_id) {
          const assignedUser = members.find(
            u => u.user_id === _session.assignedUser.user_id,
          );
          if (assignedUser) {
            setSelectedUser(assignedUser);
          } else {
            setSelectedUser({
              user_id: _session.assignedUser.user_id,
              name: _session.assignedUser.name,
            });
          }
        } else {
          setSelectedUser(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch organization members:', error);
      }
      setLoading(false);
    };

    if (open) {
      fetchUsers();
    } else {
      setSelectedUser(null);
    }
  }, [
    open,
    getIdTokenClaims,
    _session,
    selectedOrganization?.id,
    liveData,
  ]);

  const handleAssign = () => {
    if (selectedUser) {
      onAssign(selectedUser);
      onClose();
    }
  };

  const handleRemove = () => {
    if (_session?.assignedUser && onRemove) {
      const assignedUser =
        users.find(u => u.user_id === _session.assignedUser.user_id) || {
          user_id: _session.assignedUser.user_id,
          name: _session.assignedUser.name,
        };
      onRemove(assignedUser);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign/Remove Session</DialogTitle>
          <DialogDescription>
            Select a member to assign or remove from this session.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="user">Member</Label>
            <Select
              value={selectedUser?.user_id}
              onValueChange={value => {
                const user = users.find(u => u.user_id === value);
                setSelectedUser(user ?? null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user.user_id} value={user.user_id}>
                    {getOrganizationMemberLabel(user)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {onRemove && _session?.assignedUser && (
            <Button variant="destructive" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button onClick={handleAssign} disabled={!selectedUser}>
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignModal;
